import React, { useState } from "react";
import Bank from "@/assets/images/svgs/bank.svg";
import Rate from "@/assets/images/svgs/rate.svg";
import CallUser from "@/assets/images/svgs/call-user.svg";
import Book from "@/assets/images/svgs/book03.svg";
import Ctkm from "@/assets/images/svgs/ctkm.svg";
import { useNavigate } from "react-router-dom";
import { Carousel } from "@mantine/carousel";
import {
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Group,
  Image,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import ImgBg from "@/assets/images/pngs/bg-pre-login.png";
import ImgBgEn from "@/assets/images/pngs/bg-pre-login-en.png";
import ImgBg1 from "@/assets/images/pngs/bg-pre-login1.png";
import ImgBg1En from "@/assets/images/pngs/bg-pre-login1-en.png";
import ImgBg2 from "@/assets/images/pngs/bg-pre-login2.png";
import ImgBg2En from "@/assets/images/pngs/bg-pre-login2-en.png";
import classes from "./PreLogin.module.scss";
import { IconArrowUpRight } from "@tabler/icons-react";
import ImgJavis from "@/assets/images/pngs/img-javis.png";
import { useDefaultBank } from "@/hooks/useDefaultBank";
// import ImgVn from "@/assets/images/svgs/icon-vn.svg";
import { useDisclosure } from "@mantine/hooks";
import InterestRate from "@/components/layout/interest-rate/InterestRate";
import TermCondition from "@/components/layout/term-condition/TermCondition";
import PromotionModal, {
  PromotionDetailModal,
  RichText,
  Promotion,
  pickPromoText,
} from "@/components/layout/promotion/Promotion";
import AtmBranch from "@/components/layout/atm-branch/AtmBranch";
import imgKmFallback from "@/assets/images/pngs/km1.png";
import { useSearchPromotionQuery } from "@/services/authAPI";
import { get } from "lodash";
import LanguageSelect from "@/components/utils/language-picker/LanguageSelect";
import DrawerContract from "./components/DrawerContract";
import { useTranslation } from "react-i18next";

/**
 * Thanh tiện ích dưới chân màn trước đăng nhập.
 *
 * `key` là MÃ ổn định để rẽ nhánh, `labelKey` là khoá dịch để hiển thị. Trước đây
 * hai việc này dùng chung một chuỗi tiếng Việt, nên dịch nhãn là hỏng luôn phần
 * mở popup — bấm vào không ra gì mà không báo lỗi.
 */
const footerItems = [
  { key: "BRANCH", labelKey: "BRANCH_ATM", icon: Bank },
  { key: "RATE", labelKey: "INTEREST_RATE", icon: Rate },
  { key: "SUPPORT", labelKey: "SUPPORT", icon: CallUser },
  { key: "TERM", labelKey: "TNCS", icon: Book },
  { key: "PROMOTION", labelKey: "PROMOTION_PROGRAMME", icon: Ctkm },
];
/** Ảnh nền carousel pre-login. Bổ sung bản EN cho slide nào thì điền vào `en` của slide đó. */
const CAROUSEL_SLIDES: { vi: string; en: string | null }[] = [
  { vi: ImgBg, en: ImgBgEn },     // 1 — Mở tài khoản doanh nghiệp
  { vi: ImgBg1, en: ImgBg1En },   // 2 — Vay thế chấp
  { vi: ImgBg2, en: ImgBg2En },   // 3 — Tối ưu hoá dòng tiền
];

const PreLoginWrapper: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // Ảnh carousel có chữ IN CỨNG trong ảnh nên phải đổi theo ngôn ngữ — i18n không lo được.
  // Slide nào chưa có bản EN thì `en: null`, tự rơi về bản tiếng Việt.
  const isEn = i18n.language?.startsWith("en");
  const { image: bankImage, shortName } = useDefaultBank();
  // "Dành riêng cho bạn" — lấy data thật từ API (prelogin: BE trả showcase toàn bộ promotion SPECIFIC)
  const { data: exclusiveData } = useSearchPromotionQuery({
    categoryCode: "SPECIFIC",
    pageNumber: 1,
  });
  // Dùng CHUNG nguồn với popup "Chương trình khuyến mãi" (cùng query SPECIFIC, cùng cache RTK)
  // → KHÔNG slice để 2 nơi hiển thị y hệt nhau, không lệch số lượng
  const exclusiveOffers = get(
    exclusiveData,
    "body.promotions",
    [],
  ) as Promotion[];
  // Popup "Chi tiết chương trình" khi click 1 thẻ ở mục DÀNH RIÊNG CHO BẠN
  const [detailItem, setDetailItem] = useState<Promotion | null>(null);
  const [openedDetail, { open: openDetail, close: closeDetail }] =
    useDisclosure(false);
  const handleSelectPromotion = (item: Promotion) => {
    setDetailItem(item);
    openDetail();
  };
  const [openedRate, { open: openRate, close: closeRate }] =
    useDisclosure(false);
  const [openedTerm, { open: openTerm, close: closeTerm }] =
    useDisclosure(false);
  const [openedPromotion, { open: openPromotion, close: closePromotion }] =
    useDisclosure(false);
  const [openedBranch, { open: openBranch, close: closeBranch }] =
    useDisclosure(false);
  const [openedContract, { open: openContract, close: closeContract }] =
    useDisclosure(false);
  const handleOpened = (item: (typeof footerItems)[number]) => {
    switch (item.key) {
      case "RATE":
        openRate();
        break;
      case "TERM":
        openTerm();
        break;
      case "SUPPORT":
        navigate("/support");
        break;
      case "BRANCH":
        openBranch();
        break;
      case "PROMOTION":
        openPromotion();
        break;
      default:
        break;
    }
  };
  return (
    <>
      <Box pos="relative">
        <Carousel
          w="100%"
          mx="auto"
          withIndicators
          withControls={false}
          height="100vh"
          styles={{
            indicators: { bottom: "39rem", left: "-78%" },
            indicator: {
              width: "40px",
              height: "4px",
              transition: "width 250ms ease",
              backgroundColor: "#fff",
            },
          }}
        >
          {CAROUSEL_SLIDES.map((slide) => (
            <Carousel.Slide key={slide.vi}>
              <Box
                h="100vh"
                bg={`url(${(isEn && slide.en) || slide.vi})`}
                style={{
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              />
            </Carousel.Slide>
          ))}
        </Carousel>
        <Box w="100%" px={24}>
          <Box p={20} pos="absolute" top={0} w="calc(100% - 48px)">
            <Box className={classes.headerPreLogin}>
              <Box w={166}>
                <Image
                  src={bankImage || ImgJavis}
                  alt={`${shortName} Logo`}
                  w={166}
                  fit="contain"
                />
              </Box>
              <Flex gap={40} align="center">
                {/* <ActionIcon>
                <IconSearch />
              </ActionIcon> */}
                <Text
                  fw={700}
                  fz={14}
                  c="#15171E"
                  style={{ cursor: "pointer" }}
                  onClick={() => openContract()}
                >
                  {t("CONTACT")}
                </Text>

                {/* <Menu shadow="md" width={200} position="bottom-start">
                  <Menu.Target>
                    <Flex
                      align="center"
                      style={{
                        background: "#EBEBEB",
                        padding: "10px 12px",
                        borderRadius: "16px",
                        cursor: "pointer",
                      }}
                      gap={8}
                    >
                      <ActionIcon w={24}>
                        <Image src={ImgVn} w={24} />
                      </ActionIcon>
                      <Text fw={500} fz="14px">
                        Tiếng Việt
                      </Text>
                    </Flex>
                  </Menu.Target>

                  <Menu.Dropdown>
                    <Menu.Item icon={<Image src={ImgVn} w={24} />}>
                      Tiếng Việt
                    </Menu.Item>
                    <Menu.Item icon={<Image src={ImgVn} w={24} />}>
                      Tiếng Anh
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu> */}
                <LanguageSelect />

                <Button
                  rightIcon={<IconArrowUpRight />}
                  h={48}
                  onClick={() => navigate("/login")}
                  style={{
                    background:
                      "linear-gradient(279.48deg, #08AAF3 -9.02%, #2A81D0 10.15%, #10436E 99.12%)",
                  }}
                >
                  {t("LOGIN_NOW")}
                </Button>
              </Flex>
            </Box>
          </Box>
          <Box className={classes.footerPreLogin} w="100%">
            {footerItems.map((item) => (
              <Stack
                spacing={12}
                align="center"
                key={item.key}
                style={{ cursor: "pointer" }}
                onClick={() => handleOpened(item)}
              >
                <Box w={36}>
                  <Image src={item.icon} w={36} />
                </Box>
                <Text fw={500} fz={16} style={{ color: "white" }}>
                  {t(item.labelKey)}
                </Text>
              </Stack>
            ))}
          </Box>
        </Box>
        <InterestRate opened={openedRate} close={closeRate} />
        <TermCondition opened={openedTerm} close={closeTerm} />
        <PromotionModal opened={openedPromotion} close={closePromotion} />
        <AtmBranch opened={openedBranch} close={closeBranch} />
        {/* <ContactModal
          opened={openedContact}
          onClose={() => setOpenedContact(false)}
        /> */}
      </Box>
      <Box px={100} py={80}>
        <Group mb={12} spacing={8}>
          <Title
            order={3}
            style={{ fontWeight: 800, letterSpacing: 0.2, fontSize: "36px" }}
          >
            {t("EXCLUSIVE_FOR")}
          </Title>
          <Title
            order={3}
            style={{ fontWeight: 800, color: "#1D4ED8", fontSize: "36px" }}
          >
            {t("EXCLUSIVE_YOU")}
          </Title>
        </Group>
        <Grid>
          {exclusiveOffers.map((item) => (
            <Grid.Col span={4} key={item.pmId}>
              <Card
                withBorder
                radius="md"
                p={0}
                onClick={() => handleSelectPromotion(item)}
                style={{
                  overflow: "hidden",
                  background: "#fff",
                  boxShadow: "0px 25px 52px -12px #11111133",
                  cursor: "pointer",
                }}
              >
                {/* Image */}
                <Box
                  style={{
                    height: 275,
                    background: "#F3F4F6",
                  }}
                >
                  <Image
                    src={
                      pickPromoText(
                        i18n.language,
                        item.imageRepresent,
                        item.imageRepresentEn,
                      ) || imgKmFallback
                    }
                    alt={pickPromoText(i18n.language, item.title, item.titleEn)}
                    height={275}
                    fit="cover"
                    withPlaceholder
                    styles={{
                      image: { width: "100%", height: "100%" },
                    }}
                  />
                </Box>

                {/* Content */}
                <Box p={12}>
                  <Text
                    fw={700}
                    lineClamp={2}
                    style={{ color: "#111827", fontSize: "18px" }}
                  >
                    {pickPromoText(i18n.language, item.title, item.titleEn)}
                  </Text>
                  <Text
                    c="dimmed"
                    mt={6}
                    lineClamp={3}
                    style={{ fontSize: "16px" }}
                  >
                    {/* #56358/#56359: searchPromotion trả sẵn bản tiếng Anh — dùng đúng bản
                        theo ngôn ngữ đang bật, bài chưa dịch thì lùi về tiếng Việt. */}
                    <RichText
                      text={pickPromoText(i18n.language, item.content, item.contentEn)}
                    />
                  </Text>
                </Box>
              </Card>
            </Grid.Col>
          ))}
        </Grid>
      </Box>
      <DrawerContract opened={openedContract} close={closeContract} />
      <PromotionDetailModal
        opened={openedDetail}
        item={detailItem}
        close={closeDetail}
      />
    </>
  );
};

export default PreLoginWrapper;
